import React, { useEffect, useState } from 'react';
import { contactApi } from '../services';
import Icon from './Icon';
import { managerNames, OUTREACH_STAGE_LABELS } from './utils';
import PipelineKanban from './PipelineKanban';
import AddTargetAccountDialog from './AddTargetAccountDialog';
import './PipelineView.css';

const STAGE_KEYS = Object.keys(OUTREACH_STAGE_LABELS);
const DEFAULT_SORT = { key: 'pending', direction: 'desc' };

const isBlank = (value) => value === null || value === undefined || value === '';
const orDash = (value) => (isBlank(value) ? '–' : value);

// One entry per column: its header label, and the value its rows are sorted by
const COLUMNS = [
  { key: 'company', label: 'Company', value: (row) => row.company_name },
  { key: 'icp_rank', label: 'ICP rank', value: (row) => row.icp_rank },
  { key: 'tier', label: 'Tier', value: (row) => row.tier },
  { key: 'stage', label: 'Stage', value: (row) => OUTREACH_STAGE_LABELS[row.outreach_stage] },
  { key: 'pending', label: 'Pending review', value: (row) => row.pending_findings_count },
  { key: 'manager', label: 'Relationship manager', value: (row) => managerNames(row.relationship_managers || []) },
  { key: 'people', label: 'People to reach', value: (row) => row.target_people_count },
  { key: 'known', label: 'Who knows them', value: (row) => (row.known_by || []).join(', ') }
];

// Rows with no value go last, whichever way the column runs. A tie - including on Pending review,
// which is 0 for almost every account - falls back to company name, then id, so the table always
// reads as sorted instead of preserving whatever order the API happened to return them in.
const compareRows = (a, b, column, direction) => {
  const x = column.value(a);
  const y = column.value(b);
  const order = (isBlank(x) || isBlank(y))
    ? Number(isBlank(x)) - Number(isBlank(y))
    : (typeof x === 'number' && typeof y === 'number'
      ? x - y
      : String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' }));
  if (order !== 0) return direction === 'asc' ? order : -order;
  const nameOrder = String(a.company_name || '').localeCompare(String(b.company_name || ''), undefined, { numeric: true, sensitivity: 'base' });
  return nameOrder !== 0 ? nameOrder : a.id - b.id;
};

/**
 * Target accounts by outreach stage. A row opens the company panel; a stage pill switches its rows out of view.
 * companyFilter (from Graph.js's appliedFilters - the Filters panel or an Ask Sinatra question,
 * they're the same state) narrows the rows by company name, same as Graph/Tree already do.
 */
const PipelineView = ({ onOpenCompany, companyFilter = '' }) => {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState(null);
  // The stages switched off; every stage starts on
  const [hiddenStages, setHiddenStages] = useState([]);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [layout, setLayout] = useState('table');
  const [showAddDialog, setShowAddDialog] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await contactApi.searchTargetAccounts();
        if (!cancelled) setAccounts(data.accounts || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load target accounts.');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Optimistic: the board moves the card right away; a failed save reloads from the server so the
  // board never quietly drifts from what's actually saved.
  const moveAccount = async (accountId, changes) => {
    setAccounts(current => current.map(account => (account.id === accountId ? { ...account, ...changes } : account)));
    try {
      await contactApi.moveTargetAccount(accountId, changes);
    } catch (err) {
      setError(err.message || 'Could not move the account.');
      try {
        const data = await contactApi.searchTargetAccounts();
        setAccounts(data.accounts || []);
      } catch {
        // The reload failed too; the error above already explains there's a problem.
      }
    }
  };

  // Appended straight into the list (same shape as a searchTargetAccounts() row), so the new card
  // shows up without a refetch.
  const handleCreated = (account) => {
    setAccounts(current => [...(current || []), account]);
  };

  const toggleStage = (stage) => {
    setHiddenStages(hidden => (hidden.includes(stage)
      ? hidden.filter(item => item !== stage)
      : [...hidden, stage]));
  };

  // A column that isn't sorted yet sorts ascending; the one already sorted flips direction
  const sortBy = (key) => {
    setSort(current => (current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' }));
  };

  const column = COLUMNS.find(item => item.key === sort.key);
  const rows = [...(accounts || [])].sort((a, b) => compareRows(a, b, column, sort.direction));
  const needle = companyFilter.trim().toLowerCase();
  const nameFiltered = rows.filter(row => !needle || (row.company_name || '').toLowerCase().includes(needle));
  const visibleRows = nameFiltered.filter(row => !hiddenStages.includes(row.outreach_stage));

  return (
    <div className="pipeline-view">
      {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}
      {!accounts && !error && <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>}
      {accounts && (
        <>
          <div className="pipeline-view__toolbar">
            <div className="pipeline-view__toolbar-left">
              <div className="pipeline-view__stages" role="group" aria-label="Outreach stage">
                {STAGE_KEYS.map(stage => {
                  const shown = !hiddenStages.includes(stage);
                  return (
                    <button
                      key={stage}
                      type="button"
                      className={`graph-filter-pill pipeline-stage-pill pipeline-stage-pill--${stage}${shown ? '' : ' pipeline-stage-pill--off'}`}
                      aria-pressed={shown}
                      onClick={() => toggleStage(stage)}
                    >
                      {OUTREACH_STAGE_LABELS[stage]}
                    </button>
                  );
                })}
              </div>
              <div className="pipeline-view__layout-toggle" role="group" aria-label="Layout">
                <button
                  type="button"
                  className={`pipeline-layout-button${layout === 'table' ? ' active' : ''}`}
                  aria-pressed={layout === 'table'}
                  onClick={() => setLayout('table')}
                >
                  Table
                </button>
                <button
                  type="button"
                  className={`pipeline-layout-button${layout === 'kanban' ? ' active' : ''}`}
                  aria-pressed={layout === 'kanban'}
                  onClick={() => setLayout('kanban')}
                >
                  Kanban
                </button>
              </div>
              {layout === 'kanban' && (
                <button type="button" className="pipeline-view__add" onClick={() => setShowAddDialog(true)}>
                  <Icon name="plus" /> Add to pipeline
                </button>
              )}
            </div>
            <span className="pipeline-view__count">
              Showing {visibleRows.length} of {accounts.length} accounts
              {needle && ` · filtered to "${companyFilter.trim()}"`}
            </span>
          </div>

          {layout === 'kanban' ? (
            <PipelineKanban
              accounts={nameFiltered}
              hiddenStages={hiddenStages}
              onOpenCompany={onOpenCompany}
              onMoveAccount={moveAccount}
            />
          ) : (
          <div className="pipeline-view__table-wrap">
            <table className="pipeline-table">
              <thead>
                <tr>
                  {COLUMNS.map(item => {
                    const active = item.key === sort.key;
                    return (
                      <th
                        key={item.key}
                        scope="col"
                        className="pipeline-table__head"
                        aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                      >
                        <button type="button" className="pipeline-table__sort" onClick={() => sortBy(item.key)}>
                          {item.label}
                          {active && <Icon name={sort.direction === 'asc' ? 'arrow-up' : 'arrow-down'} />}
                        </button>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 && (
                  <tr>
                    <td className="pipeline-table__empty" colSpan={COLUMNS.length}>
                      {accounts.length === 0 ? 'No target accounts yet.' : 'Turn a stage back on to see its accounts.'}
                    </td>
                  </tr>
                )}
                {visibleRows.map(row => {
                  const openCompany = () => onOpenCompany({ id: row.company_id, name: row.company_name });
                  return (
                    <tr
                      key={row.id}
                      className="pipeline-table__row"
                      tabIndex={0}
                      onClick={openCompany}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') openCompany();
                      }}
                    >
                      <td className="pipeline-table__company">{row.company_name}</td>
                      <td>{orDash(row.icp_rank)}</td>
                      <td>{orDash(row.tier)}</td>
                      <td>
                        <span className={`pipeline-stage-badge pipeline-stage-badge--${row.outreach_stage || 'none'}`}>
                          {OUTREACH_STAGE_LABELS[row.outreach_stage] || 'No outreach recorded'}
                        </span>
                      </td>
                      <td>{orDash(row.pending_findings_count)}</td>
                      <td>{managerNames(row.relationship_managers || []) || '–'}</td>
                      <td>{orDash(row.target_people_count)}</td>
                      <td>{(row.known_by || []).join(', ') || '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}
      <AddTargetAccountDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default PipelineView;
